import { capitalize, hasValue } from 'utility/functions';
import { defaultLang, isSnaiSite } from 'utility/constant';

import { SeoMetaTagType } from 'components/seo/Seo';
import { TabsWithAccordionType } from 'lib/datoCms/queries/getSeoData';

interface TagsFromSeoDataProps<T> {
  cmsData: T;
  seoData?: TabsWithAccordionType | null;
  fieldName?: string | null;
  forceUpdate?: boolean;
}

export const mergeTagsFromSeoData = <T>(props: TagsFromSeoDataProps<T>): T => {
  const { seoData, cmsData, fieldName, forceUpdate } = props;

  if (seoData) {
    const { titoloDiRiferimento, descrizione } = seoData ?? {};

    const obj = cmsData ?? {};
    const pageData = fieldName ? Reflect.get(obj, fieldName) : obj;

    pageData.seoMetaTags = pageData.seoMetaTags ?? [];

    if (hasValue(descrizione)) {
      const idx = (pageData?.seoMetaTags ?? []).findIndex((x) => x?.attributes?.name === 'description');
      if (idx < 0 || forceUpdate) {
        let index = idx < 0 ? 0 : idx;
        let numItems = idx < 0 ? 0 : 1;
        pageData.seoMetaTags.splice(index, numItems, {
          tag: 'meta',
          content: null,
          attributes: { name: 'description', content: descrizione },
        });
      }
    }

    if (hasValue(titoloDiRiferimento)) {
      const idx = (pageData?.seoMetaTags ?? []).findIndex((x) => x?.tag === 'title');
      if (idx < 0 || forceUpdate) {
        let index = idx < 0 ? 0 : idx;
        let numItems = idx < 0 ? 0 : 1;
        pageData.seoMetaTags.splice(index, numItems, { tag: 'title', content: titoloDiRiferimento, attributes: null });
      }
    }

    if (fieldName) {
      Reflect.set(obj, fieldName, pageData);
    }

    return obj as T;
  }

  return cmsData;
};

/**
 * Usata per generare i Seo Meta Tags quando le pagine non presenti nel CMS
 * @param title
 * @param description
 * @returns
 */
export const generateSeoMetaTags = (title?: string, description?: string, locale?: string): Array<SeoMetaTagType> => {
  const result: Array<SeoMetaTagType> = [];
  if (title) {
    result.push({
      attributes: null,
      content: `${title} - SNAI`,
      tag: 'title',
    });
    result.push({
      attributes: {
        property: 'og:title',
        content: title,
      },
      content: null,
      tag: 'meta',
    });
    result.push({
      attributes: {
        name: 'twitter:title',
        content: title,
      },
      content: null,
      tag: 'meta',
    });
  }

  if (description) {
    result.push({
      attributes: {
        name: 'description',
        content: description,
      },
      content: null,
      tag: 'meta',
    });
    result.push({
      attributes: {
        property: 'og:description',
        content: description,
      },
      content: null,
      tag: 'meta',
    });
    result.push({
      attributes: {
        name: 'twitter:description',
        content: description,
      },
      content: null,
      tag: 'meta',
    });
  }

  result.push({
    attributes: {
      property: 'og:locale',
      content: locale ?? defaultLang,
    },
    content: null,
    tag: 'meta',
  });

  result.push({
    attributes: {
      property: 'og:type',
      content: 'article',
    },
    content: null,
    tag: 'meta',
  });
  result.push({
    attributes: {
      property: 'og:site_name',
      content: isSnaiSite ? 'SNAI' : 'HAPPYBET',
    },
    content: null,
    tag: 'meta',
  });
  result.push({
    attributes: {
      name: 'twitter:card',
      content: 'summary',
    },
    content: null,
    tag: 'meta',
  });
  result.push({
    attributes: {
      name: 'twitter:site',
      content: '@https://twitter.com/SNAI_Official',
    },
    content: null,
    tag: 'meta',
  });

  return result;
};

interface SlugType {
  verticale: string;
  disciplina?: string;
  nazione?: string;
  manifestazione?: string;
  avvenimento?: string;
  anticollisione?: string;
}
const decodeSlug = (slug: string): SlugType => {
  const [a, b, c, d, e, f] = slug.split('/');

  if (isSnaiSite) {
    return {
      verticale: a,
      disciplina: b,
      manifestazione: c,
      avvenimento: d,
      anticollisione: e,
    };
  }

  return {
    verticale: a,
    disciplina: b,
    nazione: c,
    manifestazione: d,
    avvenimento: e,
    anticollisione: f,
  };
};

const generateTitle = (slug: string, key: string, allLabels: Record<string, string>): SeoMetaTagType => {
  const { verticale, disciplina, nazione, manifestazione, avvenimento } = decodeSlug(slug);
  const label = Reflect.get(allLabels ?? {}, key);
  let content = label ?? '';
  content = content?.replace(/\{\{VERTICALE\}\}/gim, capitalize(verticale ?? '')!);
  content = content?.replace(/\{\{DISCIPLINA\}\}/gim, capitalize(disciplina ?? '')!);
  content = content?.replace(/\{\{NAZIONE\}\}/gim, capitalize(nazione ?? '')!);
  content = content?.replace(/\{\{MANIFESTAZIONE\}\}/gim, capitalize(manifestazione ?? '')!);
  content = content?.replace(/\{\{AVVENIMENTO\}\}/gim, capitalize(avvenimento ?? '')!);

  return {
    tag: 'title',
    content,
    attributes: null,
  };
};

const generateDescription = (slug: string, key: string, allLabels: Record<string, string>): SeoMetaTagType => {
  const { verticale, disciplina, nazione, manifestazione, avvenimento } = decodeSlug(slug);
  const label = Reflect.get(allLabels ?? {}, key);

  let content = label ?? '';
  content = content?.replace(/\{\{VERTICALE\}\}/gim, capitalize(verticale ?? '')!);
  content = content?.replace(/\{\{DISCIPLINA\}\}/gim, capitalize(disciplina ?? '')!);
  content = content?.replace(/\{\{NAZIONE\}\}/gim, capitalize(nazione ?? '')!);
  content = content?.replace(/\{\{MANIFESTAZIONE\}\}/gim, capitalize(manifestazione ?? '')!);
  content = content?.replace(/\{\{AVVENIMENTO\}\}/gim, capitalize(avvenimento ?? '')!);

  return {
    tag: 'meta',
    content: null,
    attributes: { name: 'description', content },
  };
};

/* eslint-disable no-unused-vars */
export enum PageKind {
  disciplina = 'disciplina',
  manifestazione = 'manifestazione',
  avvenimento = 'avvenimento',
}
/* eslint-enable no-unused-vars */

export type PageKindType = keyof typeof PageKind;

interface TagsFromLabelProps<T> {
  slug: string;
  labels: Record<string, string>;
  cmsData: T;
  pageType: PageKind | PageKindType;
  fieldName: string;
  translatedSlug?: string;
}

export const parseSlugFromParams = (value: string): string =>
  value
    .split('/')
    .map((x) => decodeURIComponent(x).toLowerCase())
    .join('/');

export const mergeTagsFromLabels = <T>(props: TagsFromLabelProps<T>): T => {
  const { slug, labels, cmsData, pageType, fieldName, translatedSlug } = props;
  const obj = cmsData ?? {};
  const [verticale] = `${slug}`.split('/');
  const pageData = Reflect.get(obj, fieldName) ?? {};

  const seoTitle = generateTitle(translatedSlug ?? slug, `seo-${verticale}-${pageType}-title`, labels);
  const seoDescription = generateDescription(
    translatedSlug ?? slug,
    `seo-${verticale}-${pageType}-description`,
    labels
  );

  pageData.seoMetaTags = pageData.seoMetaTags ?? [];
  pageData.seoMetaTags.splice(0, 0, seoDescription);
  pageData.seoMetaTags.splice(0, 0, seoTitle);

  Reflect.set(obj, fieldName, pageData);

  return obj as T;
};